import { forwardRef, useState } from "react";
import { View } from "react-native";

import { ENDPOINT_PROETHICS_STT } from "@constants/urls";
import { transcribeAudio } from "@features/session/helpers/transcription";
import { useAppTheme } from "@hooks/useAppTheme";
import { TooltipWrapper } from "@memorang/ui";
import { IconButton, Text } from "react-native-paper";

import { uriToBlob } from "../helpers/audio";
import { useProethicsRecorder } from "../hooks/useProethicsRecorder";
import { MessageField } from "./MessageField";
import type { MessageFieldHandle } from "./MessageField/types";
import { RecordingWave } from "./RecordingWave";

const formatDuration = (ms: number) => {
	const total = Math.floor(ms / 1000);
	const minutes = Math.floor(total / 60);
	const seconds = total % 60;
	return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

type Props = {
	disabled: boolean;
	onSendText: (text: string) => void;
};

export const Composer = forwardRef<MessageFieldHandle, Props>(
	({ disabled, onSendText }, ref) => {
		const [text, setText] = useState("");
		const [transcribing, setTranscribing] = useState(false);
		const recorder = useProethicsRecorder();
		const theme = useAppTheme();

		const submit = () => {
			if (disabled || transcribing || !text.trim()) {
				return;
			}
			onSendText(text);
			setText("");
		};

		const stopAndTranscribe = async (): Promise<string | undefined> => {
			// const SPEECH_LEVEL = 0.2;
			// const MIN_SPEECH_FRAMES = 3;
			// const speechFrames = recorder.meterHistory.filter(
			// 	(level) => level > SPEECH_LEVEL,
			// ).length;
			// const hasSpeech =
			// 	recorder.durationMs >= 1000 && speechFrames >= MIN_SPEECH_FRAMES;
			const uri = await recorder.stop();
			if (!uri) {
				return undefined;
			}
			setTranscribing(true);
			try {
				const blob = await uriToBlob(uri);
				const result = await transcribeAudio(blob, {
					endpoint: ENDPOINT_PROETHICS_STT,
				});
				return result.text || undefined;
			} finally {
				setTranscribing(false);
			}
		};

		// Pause: stop -> transcribe -> drop the text into the input so the learner
		// can review/edit before sending.
		const pauseAndTranscribe = async () => {
			const transcript = await stopAndTranscribe();
			if (transcript) {
				setText((prev) => (prev ? `${prev} ${transcript}` : transcript));
			}
		};

		// Send: stop -> transcribe -> submit immediately, merging any text already
		// in the field (e.g. from a prior pause) so nothing is lost.
		const sendRecording = async () => {
			const transcript = await stopAndTranscribe();
			const combined = [text.trim(), transcript?.trim()]
				.filter(Boolean)
				.join(" ");
			if (combined && !disabled) {
				onSendText(combined);
				setText("");
			}
		};

		if (recorder.isRecording) {
			// Cancel sits on the far left, wave + timer fill the middle, and pause +
			// send sit on the right (ChatGPT-style). Container metrics mirror
			// MessageField so toggling states doesn't shift the layout.
			return (
				<View
					style={{
						paddingTop: 8,
						paddingBottom: 12,
						paddingHorizontal: 12,
						backgroundColor: theme.colors.surface,
					}}
				>
					<View
						style={{
							flexDirection: "row",
							alignItems: "center",
							gap: 6,
							minHeight: 48,
							paddingLeft: 6,
							paddingRight: 6,
							borderWidth: 1,
							borderRadius: theme.roundness,
							borderColor: theme.colors.primary,
							backgroundColor: theme.colors.surface,
						}}
					>
						<TooltipWrapper title="Discard recording">
							<IconButton
								icon="close"
								size={20}
								style={{ margin: 0 }}
								iconColor={theme.colors.onSurfaceVariant}
								accessibilityLabel="Discard recording"
								onPress={() => recorder.cancel()}
							/>
						</TooltipWrapper>
						<RecordingWave levels={recorder.meterHistory} />
						<Text
							variant="bodyMedium"
							style={{
								color: theme.colors.onSurfaceVariant,
								fontVariant: ["tabular-nums"],
								minWidth: 36,
								textAlign: "right",
							}}
						>
							{formatDuration(recorder.durationMs)}
						</Text>
						<TooltipWrapper title="Stop & edit text">
							<IconButton
								icon="pause"
								size={20}
								style={{ margin: 0 }}
								iconColor={theme.colors.onSurfaceVariant}
								accessibilityLabel="Stop and edit text"
								onPress={() => pauseAndTranscribe()}
							/>
						</TooltipWrapper>
						<TooltipWrapper title="Send">
							<IconButton
								icon="arrow-up"
								mode="contained"
								size={18}
								style={{ margin: 0 }}
								containerColor={theme.colors.primary}
								iconColor={theme.colors.onPrimary}
								accessibilityLabel="Send recording"
								onPress={() => sendRecording()}
							/>
						</TooltipWrapper>
					</View>
				</View>
			);
		}

		return (
			<View
				style={{
					paddingTop: 8,
					paddingBottom: 12,
					paddingHorizontal: 12,
					backgroundColor: theme.colors.surface,
				}}
			>
				<MessageField
					ref={ref}
					value={text}
					placeholder={transcribing ? "Transcribing…" : "Type a message..."}
					sendDisabled={disabled || transcribing}
					transcribing={transcribing}
					onChangeText={setText}
					onSubmit={submit}
					onMicPress={() => recorder.start()}
				/>
			</View>
		);
	},
);

Composer.displayName = "Composer";
